import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { PatientService } from '@core/http/patient.service';
import { CatalogsService } from '@core/http/catalogs.service';

import { AuthManagementService } from '@core/service/auth-management.service';
import { GeneralFunctionsService } from '@core/service/generalFunctions.service';
import { HttpMesageHandlerService } from '@core/service/http-mesage-handler';

import { FileUploadComponent } from '@shared/components/file-upload/file-upload.component';
import { UppercaseNoAccentDirective } from '@shared/directives/uppercase-no-accent.directive';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';

import { REGEX } from '@shared/utils/regex.constants';
import { DefaultResponse } from '@core/models/Http/DefaultResponse';
import { CreateUserResponse } from '@core/models/Http/Response/CreateUserResponse';

import { BusinessGroup } from '@core/models/BusinessGroup';
import { Branch } from '@core/models/Branch';
import { FederalEntity } from '@core/models/FederalEntity';
import { Settlement } from '@core/models/Settlement';

import { environment } from '@enviroment/environment';
import moment from 'moment';
import { HttpErrorResponse } from '@angular/common/http';
import { debounceTime, distinctUntilChanged, of, tap } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import {MatAutocomplete, MatAutocompleteTrigger} from "@angular/material/autocomplete";
import {UserService} from "@core/http/table-data-services/User.service";
import {NgClass, NgForOf, NgIf} from "@angular/common";
import {RegionWordPipe} from "@core/pipes/region-word.pipe";
import {regionWord} from "@shared/utils/region-word.util";

/** ===== VALIDADORES ===== */
export const maxDateValidator = (max: Date): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const v = control.value;
    if (!v) return null;

    const d = new Date(v);
    if (isNaN(d.getTime())) return null;

    const dd = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const mm = new Date(max.getFullYear(), max.getMonth(), max.getDate()).getTime();

    return dd > mm ? { maxDate: true } : null;
  };
};

/** ✅ Tutor SIEMPRE mayor de edad */
export const minAgeValidator = (minAge: number): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const v = control.value;
    if (!v) return null;
    const d = new Date(v);
    if (isNaN(d.getTime())) return null;

    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;

    return age < minAge ? { minAge: { minAge, actual: age } } : null;
  };
};

export type TutorCreateDialogResult = {
  idUsuario: string;
  nombreCompleto: string;
  email: string;
  numeroIdentificacion: string;
  imagen?: string | null;
};

export type TutorCreateDialogData = {
  /** si quieres prellenar el correo desde el buscador */
  email?: string;
};

@Component({
  selector: 'app-tutor-create-dialog',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatCheckboxModule,
    UppercaseNoAccentDirective,
    FileUploadComponent,
    NgxMatIntlTelInputComponent,
    MatAutocomplete,
    MatAutocompleteTrigger,
    NgClass,
    RegionWordPipe,
  ],
  templateUrl: './tutor-create-dialog.component.html',
  styleUrl: './tutor-create-dialog.component.scss',
})
export class TutorCreateDialogComponent implements OnInit {

  readonly ID_TIPO_IDENTIFICACION_SIN_ASIGNAR = 0;
  readonly ID_TIPO_IDENTIFICACION_PASAPORTE = 1;
  readonly ID_TIPO_IDENTIFICACION_DOCUMENTO = 2;

  isParticular: boolean;
  showSucursalSelectForParticular: boolean = true;

  /** ===== CATALOGOS ===== */
  businessGroups: BusinessGroup[] = [];
  branches: Branch[] = [];
  federalEntity: FederalEntity[] = [];
  settlements: Settlement[] = [];

  settlementSearch = '';
  preferredCountries: string[] = ['mx', 'es', 'us'];

  /** ===== FORMS ===== */
  userForm: UntypedFormGroup;
  patientForm: UntypedFormGroup;
  archiveForm: UntypedFormGroup;

  maxBirthDate: Date = new Date();

  private idTipoUsuarioPaciente: string | null = null;

  /** para cargar región */
  userData: any;
  isDoctor: boolean;

  constructor(
    private dialogRef: MatDialogRef<TutorCreateDialogComponent, TutorCreateDialogResult | null>,
    @Inject(MAT_DIALOG_DATA) public data: TutorCreateDialogData,

    private fb: UntypedFormBuilder,
    private userService: UserService,
    private patientService: PatientService,
    private catalogsService: CatalogsService,
    private authManagement: AuthManagementService,
    protected gfs: GeneralFunctionsService,
    private httpMsgHandler: HttpMesageHandlerService,
  ) {
    this.userData = this.authManagement.userData();
    this.isDoctor = this.authManagement.isUserDoctor();
    this.isParticular = this.authManagement.isUserParticular();

    const r = (environment as any)?.region?.toString().toUpperCase();
    this.preferredCountries = (r === 'ES') ? ['es', 'mx', 'us'] : ['mx', 'es', 'us'];

    this.userForm = this.createUserForm();
    this.patientForm = this.createPatientForm();
    this.archiveForm = this.createArchivesForm();
  }

  ngOnInit(): void {
    if (!this.isParticular) {
      this.getBusinessGroups();
    }

    this.getFederalEntity();

    if (this.isParticular) {
      this.userForm.patchValue({
        idGEMP: this.userData?.IdGEMP ?? null
      }, { emitEvent: false });

      this.getBranchesForCurrentUser();
    }

    // obtener idTipoUsuario "Paciente" para usarlo como tutor (mismo tipo)
    this.userService.getTiposUsuario().subscribe({
      next: (resp: DefaultResponse<any[]>) => {
        const match = (resp.data || []).find(t =>
          String(t.nombre || '').trim().toLowerCase() === 'paciente'
        );
        this.idTipoUsuarioPaciente = match ? String(match.idTipoUsuario) : null;
      },
      error: (err) => this.httpMsgHandler.handleMsg(err),
    });

    // prellenar email si viene del padre
    const email = (this.data?.email ?? '').trim().toLowerCase();
    if (email) {
      this.userForm.patchValue({ email }, { emitEvent: false });
    }

    // CP/colonia autocomplete
    this.userForm.get('settlementSearch')?.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap((value: any) => {
          if (value && typeof value === 'string' && value.length === 5 && /^\d+$/.test(value)) {
            this.searchSettlements(value);
          } else if (typeof value === 'object') {
            // seleccionado
          } else {
            this.settlements = [];
          }
        })
      )
      .subscribe();

    setTimeout(() => {
      this.aplicarModoIdentificacionInicial();
    }, 0);
  }

  get isSpainRegion(): boolean {
    return String((environment as any)?.region ?? '').toUpperCase() === 'ES';
  }

  get documentoIdentidadLabel(): string {
    return this.isSpainRegion ? 'DNI' : 'INE';
  }

  get numeroIdentificacionReadonly(): boolean {
    const idTipo = Number(this.patientForm?.get('idTipoIdentificacion')?.value ?? 0);
    return idTipo === this.ID_TIPO_IDENTIFICACION_SIN_ASIGNAR;
  }

  private aplicarModoIdentificacionInicial(): void {
    const idTipoCtrl = this.patientForm.get('idTipoIdentificacion');
    const numeroCtrl = this.patientForm.get('numeroIdentificacion');

    if (!idTipoCtrl || !numeroCtrl) return;

    const idTipo = Number(idTipoCtrl.value ?? 0);

    if (idTipo === this.ID_TIPO_IDENTIFICACION_SIN_ASIGNAR) {
      numeroCtrl.patchValue('N/A', { emitEvent: false });
      numeroCtrl.disable({ emitEvent: false });
    } else {
      if ((numeroCtrl.value ?? '') === 'N/A') {
        numeroCtrl.patchValue('', { emitEvent: false });
      }
      numeroCtrl.enable({ emitEvent: false });
    }
  }

  onTipoIdentificacionChange(): void {
    const idTipo = Number(this.patientForm.get('idTipoIdentificacion')?.value ?? 0);
    const numeroCtrl = this.patientForm.get('numeroIdentificacion');

    if (!numeroCtrl) return;

    if (idTipo === this.ID_TIPO_IDENTIFICACION_SIN_ASIGNAR) {
      numeroCtrl.patchValue('N/A', { emitEvent: false });
      numeroCtrl.disable({ emitEvent: false });
    } else {
      if ((numeroCtrl.value ?? '') === 'N/A') {
        numeroCtrl.patchValue('', { emitEvent: false });
      }
      numeroCtrl.enable({ emitEvent: false });
    }

    numeroCtrl.updateValueAndValidity({ emitEvent: false });
  }

  getBranchesForCurrentUser() {
    this.catalogsService.getBranchesByAuthenticatedUser().subscribe({
      next: (res: DefaultResponse<Branch[]>) => {
        this.branches = [...(res.data ?? [])];

        if (this.branches.length === 0) {
          this.showSucursalSelectForParticular = false;

          this.userForm.patchValue({
            idSucursal: this.userData?.IdSucursal ?? null
          }, { emitEvent: false });

          return;
        }

        this.showSucursalSelectForParticular = true;

        if (this.branches.length === 1) {
          this.userForm.patchValue({
            idSucursal: this.branches[0].idSucursal
          }, { emitEvent: false });
        }

        const currentIdSucursal = this.userForm.get('idSucursal')?.value;
        if (currentIdSucursal) {
          const exists = this.branches.some(b => b.idSucursal === currentIdSucursal);
          if (!exists) {
            this.userForm.patchValue({ idSucursal: null }, { emitEvent: false });
          }
        }
      },
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err)
    });
  }

  /** ===== FORMS ===== */
  createUserForm(): UntypedFormGroup {
    return this.fb.group({
      idUsuario: [null],
      usr: ['', [Validators.required, Validators.pattern(REGEX.user)]],
      idTipoUsuario: [null], // se setea al guardar
      idGEMP: [null],
      idSucursal: [null],
      nombres: ['', [Validators.required, Validators.pattern(REGEX.text)]],
      primerApellido: ['', [Validators.required, Validators.pattern(REGEX.text)]],
      segundoApellido: ['', [Validators.required, Validators.pattern(REGEX.text)]],
      idAsentamiento: [null],
      domicilio: ['', [Validators.required]],
      movil: [null, [Validators.required]],
      email: ['', [Validators.required, Validators.pattern(REGEX.correo)]],
      settlementSearch: [this.settlementSearch],
    });
  }

  createPatientForm(): UntypedFormGroup {
    return this.fb.group({
      genero: [null, [Validators.required]],
      idUsuario: [null],
      fechaNacimiento: [null, [Validators.required, maxDateValidator(this.maxBirthDate), minAgeValidator(18)]], // ✅ >=18
      idTipoIdentificacion: [this.ID_TIPO_IDENTIFICACION_SIN_ASIGNAR, Validators.required],
      numeroIdentificacion: [{ value: 'N/A', disabled: true }, Validators.required],
      idEntidadNacimiento: [null, Validators.required],
      alergias: [[]],
      molecules: [[]],
      patologias: [[]],

      // ✅ Tutor NO tiene tutor
      idTutor: [null],
    });
  }

  createArchivesForm(): UntypedFormGroup {
    return this.fb.group({
      idUsuario: [null],
      imagen: [null],
      firma: [null],
      pdf: [null],
    });
  }

  /** ===== CATALOGOS ===== */
  getBusinessGroups() {
    this.catalogsService.getBusinessGroups().subscribe({
      next: (res: DefaultResponse<BusinessGroup[]>) => this.businessGroups = [...(res.data ?? [])],
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err)
    });
  }

  getFederalEntity() {
    this.catalogsService.getFederalEntitys().subscribe({
      next: (res: DefaultResponse<FederalEntity[]>) => this.federalEntity = [...(res.data ?? [])],
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err)
    });
  }

  getBranchesByGemp(idGEMP = '') {
    if (!idGEMP) idGEMP = this.userForm.get('idGEMP')?.value;
    this.catalogsService.getBranchesByIdGEMP(idGEMP).subscribe({
      next: (res: DefaultResponse<Branch[]>) => this.branches = [...(res.data ?? [])],
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err)
    });
  }

  /** ===== CP ===== */
  searchSettlements(cp: string) {
    if (!cp) { this.settlements = []; return; }

    this.catalogsService.getSettlementByPostalCode(cp).subscribe({
      next: (res: DefaultResponse<Settlement[]>) => {
        this.settlements = [...(res.data ?? [])];
        if (this.settlements.length === 0) {
          this.userForm.patchValue({ settlementSearch: null }, { emitEvent: false });
        }
      },
      error: (err) => this.httpMsgHandler.handleMsg(err)
    });
  }

  displayFn(settlement: Settlement): string {
    return settlement && settlement.asentamiento ? settlement.asentamiento : '';
  }

  setSelectedSettlement(value: Settlement): void {
    this.userForm.patchValue({ idAsentamiento: value?.idAsentamiento ?? null });
  }

  onSettlementBlur() {
    const val = this.userForm.get('settlementSearch')?.value;
    if (!val || typeof val !== 'object' || !val.idAsentamiento) {
      this.userForm.patchValue({ settlementSearch: null }, { emitEvent: false });
    }
  }

  /** ===== TELEFONO ===== */
  private toE164(val: any): string {
    if (!val) return '';
    if (typeof val === 'string') return val.trim();
    if (val?.e164) return String(val.e164).trim();
    if (val?.e164Number) return String(val.e164Number).trim();
    if (val?.number) return String(val.number).trim();
    return '';
  }

  /** ===== FECHAS ===== */
  private formatDateMoment(date: any): string {
    const m = moment(date);
    if (!m.isValid()) throw new Error('El valor de fechaNacimiento no es valido.');
    return m.startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS');
  }

  /** ===== GUARDAR ===== */
  cancelar(): void {
    this.dialogRef.close(null);
  }

  private getTutorImageForResult(): string | null {
    const imgVal = this.archiveForm.get('imagen')?.value as any;

    if (!imgVal) return null;

    if (typeof imgVal === 'string') {
      return imgVal.trim() || null;
    }

    if (typeof imgVal === 'object') {
      if (imgVal.dataUrl) {
        return String(imgVal.dataUrl).trim();
      }

      if (imgVal.base64) {
        const raw = String(imgVal.base64).trim();
        return raw.startsWith('data:image/')
          ? raw
          : `data:image/jpeg;base64,${raw}`;
      }
    }

    return null;
  }

  guardarTutor(): void {
    this.userForm.markAllAsTouched();
    this.patientForm.markAllAsTouched();

    if (this.userForm.invalid || this.patientForm.invalid) {
      Swal.fire({
        icon: 'warning',
        title: 'FORMULARIO INCOMPLETO',
        text: 'REVISA LOS CAMPOS OBLIGATORIOS.'
      }).then();
      return;
    }

    // ✅ Asegura que ya tengamos el idTipoUsuarioPaciente
    if (!this.idTipoUsuarioPaciente) {
      Swal.fire({
        icon: 'warning',
        title: 'ESPERA UN MOMENTO',
        text: 'TODAVÍA SE ESTÁ CARGANDO EL TIPO DE USUARIO. INTENTA DE NUEVO.'
      }).then();
      return;
    }

    const userData = this.prepareUserData();
    if (!userData) return;

    // ✅ IMPORTANTÍSIMO: convertir y PARCHEAR en el form
    const rawFecha = this.patientForm.get('fechaNacimiento')?.value;
    const fechaIso = this.formatDateMoment(rawFecha);
    this.patientForm.patchValue({ fechaNacimiento: fechaIso }, { emitEvent: false });

    let lastResponse: any = null;
    let createdUserId: string | null = null;

    this.userService.createUpdateUser(userData).pipe(
      tap((res: DefaultResponse<CreateUserResponse>) => {
        createdUserId = res?.data as any;

        this.patientForm.patchValue(
          { idUsuario: createdUserId, idTutor: null },
          { emitEvent: false }
        );

        this.archiveForm.patchValue({ idUsuario: createdUserId }, { emitEvent: false });
      }),
      switchMap(() => this.patientService.createUpdatePatient(this.patientForm.getRawValue())),
      tap((res: any) => lastResponse = res),
      switchMap(() => this.uploadImagesIfNeeded())
    ).subscribe({
      next: () => {
        if (lastResponse) this.httpMsgHandler.handleMsg(lastResponse);

        const nombres = String(this.userForm.get('nombres')?.value ?? '').trim();
        const ap1 = String(this.userForm.get('primerApellido')?.value ?? '').trim();
        const ap2 = String(this.userForm.get('segundoApellido')?.value ?? '').trim();
        const nombreCompleto = `${nombres} ${ap1} ${ap2}`.replace(/\s+/g, ' ').trim();

        this.dialogRef.close({
          idUsuario: String(createdUserId ?? ''),
          nombreCompleto: nombreCompleto || 'TUTOR',
          email: String(this.userForm.get('email')?.value ?? '').trim().toLowerCase(),
          numeroIdentificacion: String(this.patientForm.get('numeroIdentificacion')?.value ?? '').trim(),
          imagen: this.getTutorImageForResult()
        });
      },
      error: err => this.httpMsgHandler.handleMsg(err)
    });
  }

  private prepareUserData(): any | null {
    const userData = this.userForm.getRawValue();
    const selectedSettlement = userData.settlementSearch as Settlement;

    if (!selectedSettlement?.idAsentamiento) {
      Swal.fire({
        icon: 'error',
        title: regionWord('COLONIA REQUERIDA'),
        text: regionWord('DEBES SELECCIONAR UNA COLONIA DE LA LISTA PARA CONTINUAR.')
      }).then();
      return null;
    }

    userData.idAsentamiento = selectedSettlement.idAsentamiento;
    userData.movil = this.toE164(this.userForm.get('movil')?.value);
    delete userData.settlementSearch;

    // ✅ tutor = paciente (mismo tipo)
    userData.idTipoUsuario = this.idTipoUsuarioPaciente;

    // empresa/sucursal igual que tu lógica actual
    userData.idGEMP = (this.isDoctor || this.isParticular)
      ? this.userData.IdGEMP
      : this.userForm.get('idGEMP')?.value;

    userData.idSucursal = this.isDoctor
      ? this.userData.IdSucursal
      : this.isParticular
        ? (this.userForm.get('idSucursal')?.value || this.userData.IdSucursal)
        : this.userForm.get('idSucursal')?.value;

    userData.password = null;
    return userData;
  }

  /** ===== SUBIR IMAGEN ===== */
  private uploadImagesIfNeeded() {
    const imgVal = this.archiveForm.get('imagen')?.value as any;
    const idUsuario = this.archiveForm.get('idUsuario')?.value as string | null;

    if (!idUsuario) return of(null);
    if (!imgVal) return of(null);

    const toPlainBase64 = (v?: string | null) => {
      if (!v) return '';
      const i = v.indexOf('base64,');
      return i >= 0 ? v.substring(i + 'base64,'.length) : v;
    };

    const base64BytesSize = (b64: string): number => {
      if (!b64) return 0;
      const cleaned = b64.replace(/\s/g, '');
      const padding = cleaned.endsWith('==') ? 2 : cleaned.endsWith('=') ? 1 : 0;
      return Math.max(0, Math.floor((cleaned.length * 3) / 4) - padding);
    };

    if (typeof imgVal === 'object' && (imgVal.base64 || imgVal.dataUrl)) {
      const base64Raw = toPlainBase64(imgVal.base64 ?? imgVal.dataUrl ?? '');
      if (!base64Raw) return of(null);

      const upsert = {
        idUsuario,
        tipo: 1,
        blobBase64: base64Raw,
        contentType: imgVal.contentType ?? 'image/jpeg',
        bytesSize: Number(imgVal.bytesSize ?? base64BytesSize(base64Raw)),
        widthPx: imgVal.widthPx ?? null,
        heightPx: imgVal.heightPx ?? null,
        hashSha256Base64: imgVal.hashSha256Base64 ?? null,
      };

      if (!upsert.bytesSize || upsert.bytesSize <= 0) return of(null);
      return this.userService.uploadUsuarioAsset(upsert);
    }

    if (typeof imgVal === 'string') {
      const base64Raw = toPlainBase64(imgVal);
      if (!base64Raw) return of(null);

      const bytes = base64BytesSize(base64Raw);
      if (!bytes || bytes <= 0) return of(null);

      const upsert = {
        idUsuario,
        tipo: 1,
        blobBase64: base64Raw,
        contentType: imgVal.startsWith('data:image/png') ? 'image/png' : 'image/jpeg',
        bytesSize: bytes,
        widthPx: null,
        heightPx: null,
        hashSha256Base64: null,
      };

      return this.userService.uploadUsuarioAsset(upsert);
    }

    return of(null);
  }
}
